/**
 * Zustand delivery store for Mini App checkout
 *
 * Ported from website/src/features/checkout/lib/use-delivery-store.ts
 * with adaptations for Telegram Mini App:
 * - Default method is 'pickup' (not 'cdek')
 * - One-time migration from old localStorage key
 * - cityCode extracted from CDEK widget for recalculation
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProductType } from '@/shared/lib/product-type';

// ============================================================================
// Types
// ============================================================================

export interface CdekDeliveryInfo {
  pointCode: string;
  pointName: string;
  pointAddress: string;
  pointCity: string;
  pointWorkTime: string;
  cityCode: number;
  cost: number;
  minDays: number;
  maxDays: number;
  tariffCode: number;
  latitude: number;
  longitude: number;
  type: 'PVZ' | 'POSTAMAT' | 'DOOR';
}

export interface DoorAddress {
  street: string;
  house: string;
  building?: string;
  apartment?: string;
  entrance?: string;
  floor?: string;
  intercom?: string;
}

export type DeliveryMethodType = 'pickup' | 'cdek';

export interface CdekDeliveryResult {
  type: 'pickup' | 'cdek_pvz' | 'cdek_door';
  pvzInfo?: CdekDeliveryInfo;
  doorAddress?: DoorAddress;
  cost: number;
  minDays?: number;
  maxDays?: number;
}

export interface CartItemForCalculation {
  productType: ProductType;
  productId: string;
  quantity: number;
  weight?: number | null;
}

interface DeliveryState {
  method: DeliveryMethodType;
  cdekInfo: CdekDeliveryInfo | null;
  doorAddress: DoorAddress;
  widgetOpen: boolean;
  doorFormOpen: boolean;
  cartFingerprint: string | null;
}

interface DeliveryActions {
  selectPickup: () => void;
  selectCdek: () => void;
  setCdekInfo: (info: CdekDeliveryInfo, cartFingerprint?: string) => void;
  updateCdekCost: (cost: number, minDays?: number, maxDays?: number, cartFingerprint?: string) => void;
  setDoorAddress: (address: DoorAddress) => void;
  openWidget: () => void;
  closeWidget: () => void;
  openDoorForm: () => void;
  closeDoorForm: () => void;
  changePoint: () => void;
  reset: () => void;
}

export type DeliveryStore = DeliveryState & DeliveryActions;

// ============================================================================
// Selectors — return primitives for Zustand === comparison
// ============================================================================

export const selectIsCdekSelected = (s: DeliveryState) => s.method === 'cdek';

export const selectDeliveryCost = (s: DeliveryState): number =>
  s.method === 'pickup' ? 0 : (s.cdekInfo?.cost ?? 0);

export const selectDeliveryText = (s: DeliveryState): string => {
  if (s.method === 'pickup') return 'Бесплатно';
  if (!s.cdekInfo) return 'Выберите доставку';
  return `~${s.cdekInfo.cost.toLocaleString('ru-RU')} ₽`;
};

export const selectIsCdek = (s: DeliveryState): boolean =>
  s.method === 'cdek' && s.cdekInfo !== null;

export const selectCanSubmitDelivery = (s: DeliveryState): boolean => {
  if (s.method === 'pickup') return true;
  if (!s.cdekInfo) return false;
  if (s.cdekInfo.type === 'DOOR') {
    return Boolean(s.doorAddress.street?.trim() && s.doorAddress.house?.trim());
  }
  return true;
};

export const selectCartFingerprint = (s: DeliveryState): string | null => s.cartFingerprint;
export const selectCityCode = (s: DeliveryState): number => s.cdekInfo?.cityCode ?? 0;
export const selectWidgetOpen = (s: DeliveryState): boolean => s.widgetOpen;

// ============================================================================
// Non-selector utility
// ============================================================================

export function getDeliveryResult(s: DeliveryState): CdekDeliveryResult | null {
  if (s.method === 'pickup') {
    return { type: 'pickup', cost: 0 };
  }
  if (!s.cdekInfo) return null;

  if (s.cdekInfo.type === 'DOOR') {
    const addrValid = Boolean(s.doorAddress.street?.trim() && s.doorAddress.house?.trim());
    return {
      type: 'cdek_door',
      pvzInfo: s.cdekInfo,
      doorAddress: addrValid ? s.doorAddress : undefined,
      cost: s.cdekInfo.cost,
      minDays: s.cdekInfo.minDays,
      maxDays: s.cdekInfo.maxDays,
    };
  }

  return {
    type: 'cdek_pvz',
    pvzInfo: s.cdekInfo,
    cost: s.cdekInfo.cost,
    minDays: s.cdekInfo.minDays,
    maxDays: s.cdekInfo.maxDays,
  };
}

// ============================================================================
// Store
// ============================================================================

const initialState: DeliveryState = {
  method: 'pickup',
  cdekInfo: null,
  doorAddress: { street: '', house: '' },
  widgetOpen: false,
  doorFormOpen: false,
  cartFingerprint: null,
};

/** Migrate old localStorage format into new Zustand store */
function migrateOldStorage(): Partial<DeliveryState> {
  try {
    const raw = localStorage.getItem('cdek_delivery_state_miniapp');
    if (!raw) return {};
    const old = JSON.parse(raw) as {
      deliveryType?: 'pickup' | 'cdek';
      deliveryInfo?: any;
      doorAddress?: DoorAddress;
    };
    localStorage.removeItem('cdek_delivery_state_miniapp');
    return {
      method: old.deliveryType ?? 'pickup',
      cdekInfo: old.deliveryInfo ?? null,
      doorAddress: old.doorAddress ?? { street: '', house: '' },
    };
  } catch {
    return {};
  }
}

export const useDeliveryStore = create<DeliveryStore>()(
  persist(
    (set) => ({
      ...initialState,

      selectPickup: () => set({
        method: 'pickup',
        widgetOpen: false,
        doorFormOpen: false,
      }),

      selectCdek: () => set((s) => {
        if (s.cdekInfo) {
          return {
            method: 'cdek',
            widgetOpen: false,
            doorFormOpen: s.cdekInfo.type === 'DOOR',
          };
        }
        return {
          method: 'cdek',
          widgetOpen: true,
          doorFormOpen: false,
        };
      }),

      setCdekInfo: (info, cartFingerprint) => set({
        cdekInfo: info,
        widgetOpen: false,
        doorFormOpen: info.type === 'DOOR',
        cartFingerprint: cartFingerprint ?? null,
      }),

      updateCdekCost: (cost, minDays, maxDays, cartFingerprint) => set((s) => {
        if (!s.cdekInfo) return s;
        return {
          cdekInfo: {
            ...s.cdekInfo,
            cost,
            ...(minDays !== undefined && { minDays }),
            ...(maxDays !== undefined && { maxDays }),
          },
          cartFingerprint: cartFingerprint ?? s.cartFingerprint,
        };
      }),

      setDoorAddress: (address) => set({ doorAddress: address }),

      openWidget: () => set({ widgetOpen: true, doorFormOpen: false }),
      closeWidget: () => set({ widgetOpen: false }),
      openDoorForm: () => set({ doorFormOpen: true }),
      closeDoorForm: () => set({ doorFormOpen: false }),

      changePoint: () => set({ widgetOpen: true, doorFormOpen: false }),

      reset: () => set(initialState),
    }),
    {
      name: 'cdek_delivery_miniapp',
      version: 1,
      migrate: (persistedState: unknown) => ({
        ...initialState,
        ...(persistedState as Partial<DeliveryState>),
        ...migrateOldStorage(),
      }),
      partialize: (s) => ({
        method: s.method,
        cdekInfo: s.cdekInfo,
        doorAddress: s.doorAddress,
        cartFingerprint: s.cartFingerprint,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.cdekInfo?.type === 'DOOR') {
          state.doorFormOpen = true;
        }
      },
    }
  )
);
